import { useCallback, useEffect, useState } from 'react';
import {
  getKbReadinessSummary,
  getKbAuditFindings,
  getKnowledgeGeneratorTasks,
  type KbAuditFinding,
  type KbReadinessSummary,
  type KnowledgeGeneratorTask,
} from '../services/apiService';
import { hasUnresolvedUncertaintyLabel } from '../utils/uncertaintyLabels';
import { isSuperadminInitialDraft } from '../utils/knowledgeTaskOrigin';
import { displayAuditText } from '../utils/kbAuditText';

/**
 * Plain-language readiness scoring for the Knowledge Base Overview page.
 * The four-state status per category (answers/landmines/competitors/
 * playbooks) is computed once, server-side, by GET /kb/readiness-summary
 * (backend/src/controllers/kbController.js) — this hook reads that value
 * directly rather than re-deriving it from raw counts, so the two cannot
 * disagree on what counts as "ready". The superadmin portal's KB readiness
 * (salesguru-backend-tenant) is a structurally separate cross-tenant service
 * with the same shape by convention, not a shared implementation — this hook isn't reachable from
 * there. Never surfaces raw counts or technical terms to callers beyond
 * what's needed for a one-line detail string.
 */
export type ReadinessStatus = 'ready' | 'draft_ready' | 'needs_review' | 'needs_more_detail' | 'not_set_up';

export interface ReadinessCard {
  key: 'answers' | 'landmines' | 'competitors' | 'training' | 'playbooks';
  label: string;
  status: ReadinessStatus;
  detail: string;
  /** Shared route contract used by the overview and Guide. */
  href?: string;
}

export type NeedsAttentionSection = 'answers' | 'landmines' | 'competitors' | 'overview' | 'playbooks' | 'imports';

export interface NeedsAttentionItem {
  id: string;
  title: string;
  ctaLabel: string;
  section: NeedsAttentionSection;
  /** Only meaningful when section === 'answers' — pre-selects the filter chip. */
  answersFilter?: 'objections' | 'faqs';
  /** External navigation instead of an in-page section switch. */
  href?: string;
  kind: 'required' | 'optional';
  attentionType?: 'live_guidance' | 'coverage';
}

const draftReviewHref = (taskId: string, section?: 'company_context' | 'claims' | 'objections' | 'faqs' | 'landmines' | 'competitors' | 'playbooks') => {
  const params = new URLSearchParams({ setup: 'review', taskId });
  if (section) params.set('reviewSection', section);
  return `/knowledge-base?${params.toString()}`;
};

/** A task can sit at `ready_for_review` with nothing in it — e.g. a crawl
 *  of a site with no usable content. Selecting one of those as "the" draft
 *  sends the admin to a review screen with nothing to review, and hides
 *  whatever real draft exists behind it. Counts come from the list API's
 *  aggregation (suggestions/onboardingDraft arrays aren't in that payload). */
const hasDraftContent = (task: KnowledgeGeneratorTask): boolean =>
  task.unpublishedDraftCount != null
    ? task.unpublishedDraftCount > 0
    : (task.suggestedObjectionsCount || 0) > 0 ||
      (task.suggestedWebsiteFaqsCount || 0) > 0 ||
      (task.suggestedAiGeneratedFaqsCount || 0) > 0 ||
      (task.suggestedFaqsCount || 0) > 0 ||
      (task.suggestedLandminesCount || 0) > 0 ||
      (task.suggestedCompetitorsCount || 0) > 0;

/** A draft can have landmines/competitors but zero objections/FAQs — the
 *  "Live answers" card must not send the admin into a review screen with
 *  nothing in the answers section to review, the same dead-link this and
 *  the landmines/competitors/playbooks cards below all guard against. */
const hasDraftAnswers = (task: KnowledgeGeneratorTask): boolean =>
  task.unpublishedDraftCount != null
    ? (task.unpublishedObjectionsCount || 0) > 0 ||
      (task.unpublishedWebsiteFaqsCount || 0) > 0 ||
      (task.unpublishedAiGeneratedFaqsCount || 0) > 0 ||
      (task.unpublishedFaqsCount || 0) > 0
    : (task.suggestedObjectionsCount || 0) > 0 ||
      (task.suggestedWebsiteFaqsCount || 0) > 0 ||
      (task.suggestedAiGeneratedFaqsCount || 0) > 0 ||
      (task.suggestedFaqsCount || 0) > 0;

const getDraftAnswerCount = (task: KnowledgeGeneratorTask | null): number => {
  if (!task) return 0;
  if (task.unpublishedDraftCount == null) {
    const hasSplitSuggestedFaqCounts = task.suggestedWebsiteFaqsCount != null || task.suggestedAiGeneratedFaqsCount != null;
    const suggestedFaqCount = hasSplitSuggestedFaqCounts
      ? (task.suggestedWebsiteFaqsCount || 0) + (task.suggestedAiGeneratedFaqsCount || 0)
      : task.suggestedFaqsCount || 0;
    return (task.suggestedObjectionsCount || 0) + suggestedFaqCount;
  }
  const objectionCount = task.unpublishedObjectionsCount || 0;
  const hasSplitFaqCounts = task.unpublishedWebsiteFaqsCount != null || task.unpublishedAiGeneratedFaqsCount != null;
  const faqCount = hasSplitFaqCounts
    ? (task.unpublishedWebsiteFaqsCount || 0) + (task.unpublishedAiGeneratedFaqsCount || 0)
    : task.unpublishedFaqsCount || 0;
  return objectionCount + faqCount;
};

const isReviewableTask = (task: KnowledgeGeneratorTask): boolean =>
  task.status === 'ready_for_review' ||
  (task.status === 'completed' && (task.unpublishedDraftCount || 0) > 0);

const nestedDraftCount = (
  task: KnowledgeGeneratorTask,
  unpublishedField: 'unpublishedLandminesCount' | 'unpublishedCompetitorsCount',
  totalField: 'suggestedLandminesCount' | 'suggestedCompetitorsCount',
): number => task.unpublishedDraftCount != null
  ? task[unpublishedField] || 0
  : task[totalField] || 0;

export interface KbReadinessData {
  loading: boolean;
  error: string | null;
  draftError: string | null;
  auditError: string | null;
  cards: ReadinessCard[];
  needsAttention: NeedsAttentionItem[];
  auditFindings: KbAuditFinding[];
  kgTasks: KnowledgeGeneratorTask[];
  seededTask: KnowledgeGeneratorTask | null;
  reviewableTask: KnowledgeGeneratorTask | null;
  activeDraftTask: KnowledgeGeneratorTask | null;
  liveAnswerCount: number;
  hasLiveCompanyContext: boolean;
  hasDraftCompanyContext: boolean;
  canCreatePlaybook: boolean;
  refresh: () => void;
}

export function useKbReadiness(): KbReadinessData {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [summary, setSummary] = useState<KbReadinessSummary | null>(null);
  const [auditFindings, setAuditFindings] = useState<KbAuditFinding[]>([]);
  const [kgTasks, setKgTasks] = useState<KnowledgeGeneratorTask[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      setDraftError(null);
      setAuditError(null);
      try {
        const [summaryResp, kgResult, auditResult] = await Promise.all([
          getKbReadinessSummary(),
          getKnowledgeGeneratorTasks(1, 10)
            .then((value) => ({ value, error: null as string | null }))
            .catch((reason) => ({ value: null, error: reason instanceof Error ? reason.message : 'Failed to load onboarding drafts' })),
          getKbAuditFindings()
            .then((value) => ({ value, error: null as string | null }))
            .catch((reason) => ({ value: null, error: reason instanceof Error ? reason.message : 'Failed to load KB health findings' })),
        ]);

        if (!active) return;
        setSummary(summaryResp);
        setKgTasks(kgResult.value?.data || []);
        setDraftError(kgResult.error);
        setAuditFindings(auditResult.value?.findings || []);
        setAuditError(auditResult.error);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Failed to load knowledge base readiness');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [refreshTick]);

  // --- Readiness cards -----------------------------------------------------

  const answersTotal = summary?.answers?.total || 0;
  const activeDraftTask =
    kgTasks.find(
      (task) =>
        (
          ['pending', 'researching', 'generating', 'syncing'].includes(task.status) ||
          isReviewableTask(task)
        ) &&
        (!isReviewableTask(task) || hasDraftContent(task))
    ) || null;
  const draftResearch = activeDraftTask?.researchContent;
  const hasDraftWhatYouSell = Boolean(
    draftResearch?.summary?.trim() || draftResearch?.products?.length
  );
  const hasDraftHowItWorks = Boolean(draftResearch?.howItWorks?.trim());
  const hasDraftCompanyContext = Boolean(
    hasDraftWhatYouSell ||
    draftResearch?.targetAudience?.trim() ||
    hasDraftHowItWorks ||
    draftResearch?.keyFeatures?.length ||
    draftResearch?.certifications?.length
  );
  const draftClaimsToVerify = draftResearch?.claimsReviewedAt
    ? 0
    : draftResearch?.certifications?.length || 0;
  const hasCompleteReviewableDraftCompanyContext = Boolean(
    activeDraftTask &&
    isReviewableTask(activeDraftTask) &&
    hasDraftWhatYouSell &&
    hasDraftHowItWorks &&
    draftClaimsToVerify === 0
  );
  const draftCompanyNeedsClarification = hasUnresolvedUncertaintyLabel(draftResearch || '');
  const selectedDraftAnswers = activeDraftTask?.selectedDraftAnswersCount || 0;
  const selectedDraftLandmines = activeDraftTask?.selectedDraftLandminesCount || 0;
  const selectedDraftCompetitors = activeDraftTask?.selectedDraftCompetitorsCount || 0;
  const landmineDraftReviewed = Boolean(
    selectedDraftLandmines > 0 &&
    activeDraftTask?.onboardingDraft?.landmineSetReview?.reviewedAt &&
    activeDraftTask.onboardingDraft.landmineSetReview.reviewedTempIds?.length === selectedDraftLandmines
  );
  const competitorDraftReviewed = Boolean(
    selectedDraftCompetitors > 0 &&
    activeDraftTask?.onboardingDraft?.competitorSetReview?.reviewedAt &&
    activeDraftTask.onboardingDraft.competitorSetReview.reviewedTempIds?.length === selectedDraftCompetitors
  );
  // Statuses are computed server-side (kbController.js) and read directly
  // here — this used to re-derive the same classification from raw counts
  // and a locally duplicated copy of the thresholds, which could drift from
  // what the backend actually decided.
  const answersStatus: ReadinessStatus = answersTotal > 0
    ? summary?.answers?.status || 'not_set_up'
    : selectedDraftAnswers > 0 ? 'draft_ready' : 'not_set_up';

  const activeLandmines = summary?.landmines?.total || 0;
  const landminesMeeting = summary?.landmines?.meeting || 0;
  const landminesStatus: ReadinessStatus = activeLandmines > 0
    ? summary?.landmines?.status || 'not_set_up'
    : selectedDraftLandmines > 0
    ? landmineDraftReviewed ? 'draft_ready' : 'needs_review'
    : 'not_set_up';
  const pendingLandmines = summary?.landmines?.pending || 0;
  const landminesMissingSafeGuidance = summary?.landmines?.missingSafeGuidance || 0;
  const detractorsMissingCoachingTip = summary?.landmines?.missingDetractorTip || 0;

  const activeCompetitors = summary?.competitors?.total || 0;
  const competitorsMeeting = summary?.competitors?.meeting || 0;
  const competitorsStatus: ReadinessStatus = activeCompetitors > 0
    ? summary?.competitors?.status || 'not_set_up'
    : selectedDraftCompetitors > 0
    ? competitorDraftReviewed ? 'draft_ready' : 'needs_review'
    : 'not_set_up';
  const incompleteCompetitors = Math.max(0, activeCompetitors - competitorsMeeting);

  const hasWhatYouSell = summary?.companyContext?.hasWhatYouSell || false;
  const hasHowItWorks = summary?.companyContext?.hasHowItWorks || false;
  const hasLiveCompanyContext = hasWhatYouSell || hasHowItWorks;
  const trainingStatus: ReadinessStatus = hasWhatYouSell && hasHowItWorks
    ? 'ready'
    : draftCompanyNeedsClarification
    ? 'needs_review'
    : hasCompleteReviewableDraftCompanyContext
    ? 'draft_ready'
    : hasDraftCompanyContext
    ? 'needs_review'
    : hasLiveCompanyContext
    ? 'needs_more_detail'
    : 'not_set_up';
  // The backend owns publication eligibility. In particular, a completed
  // onboarding task and a pending/failed cleanup row are not evidence that
  // the playbook generator has live source material.
  const canCreatePlaybook = summary?.publishedKnowledge?.hasAny ?? false;

  const activePlaybooks = summary?.playbooks?.total || 0;
  const playbooksStatus = summary?.playbooks?.status || 'not_set_up';
  const effectivePlaybooksStatus: ReadinessStatus = activePlaybooks > 0
    ? playbooksStatus
    : activeDraftTask?.onboardingDraft?.starterPlaybookTaskId && activeDraftTask.onboardingDraft.starterPlaybookIncluded !== false
    ? activeDraftTask.onboardingDraft.starterPlaybookError ? 'needs_review' : 'draft_ready'
    : 'not_set_up';

  const seededTask = kgTasks.find((t) => isSuperadminInitialDraft(t.createdBy)) || null;
  const reviewableTask =
    kgTasks
      .filter((t) => isReviewableTask(t) && hasDraftContent(t))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0] || null;
  const draftAnswerCount = getDraftAnswerCount(reviewableTask);
  const draftLandmineCount = reviewableTask
    ? nestedDraftCount(reviewableTask, 'unpublishedLandminesCount', 'suggestedLandminesCount')
    : 0;
  const draftCompetitorCount = reviewableTask
    ? nestedDraftCount(reviewableTask, 'unpublishedCompetitorsCount', 'suggestedCompetitorsCount')
    : 0;
  const landmineCardTotal = activeLandmines || selectedDraftLandmines || draftLandmineCount;
  const landmineCardReady = activeLandmines > 0
    ? landminesMeeting
    : landmineDraftReviewed ? selectedDraftLandmines : 0;
  const competitorCardTotal = activeCompetitors || selectedDraftCompetitors || draftCompetitorCount;
  const competitorCardReady = activeCompetitors > 0
    ? competitorsMeeting
    : competitorDraftReviewed ? selectedDraftCompetitors : 0;

  const objectionsNeedingVariations = Math.max(
    0,
    (summary?.answers?.objections?.total || 0) - (summary?.answers?.objections?.meeting || 0),
  );
  const faqsNeedingVariations = Math.max(
    0,
    (summary?.answers?.faqs?.total || 0) - (summary?.answers?.faqs?.meeting || 0),
  );
  const answersNeedingVariations = objectionsNeedingVariations + faqsNeedingVariations;
  const objectionCoverageShortfall = Math.max(
    0,
    (summary?.thresholds?.minObjections || 0) - (summary?.answers?.objections?.total || 0),
  );
  const faqCoverageShortfall = Math.max(
    0,
    (summary?.thresholds?.minFaqs || 0) - (summary?.answers?.faqs?.total || 0),
  );
  const hasPublishedKnowledge = summary?.publishedKnowledge?.hasAny || false;

  const cards: ReadinessCard[] = [
    {
      key: 'training',
      label: 'Company profile',
      status: trainingStatus,
      detail: trainingStatus === 'ready' || trainingStatus === 'draft_ready'
        ? 'Profile complete'
        : 'Profile incomplete',
      href: hasLiveCompanyContext || !reviewableTask || !hasDraftCompanyContext
        ? '/knowledge-base?tab=overview&focus=company-details'
        : draftReviewHref(reviewableTask._id, draftClaimsToVerify > 0 ? 'claims' : 'company_context'),
    },
    {
      key: 'answers',
      label: 'Objections & FAQs',
      status: answersStatus,
      detail: `${answersTotal} published answer${answersTotal === 1 ? '' : 's'}${draftAnswerCount > 0 ? ` · ${draftAnswerCount} draft${draftAnswerCount === 1 ? '' : 's'}` : ''}`,
      href: answersTotal > 0 || !reviewableTask || !hasDraftAnswers(reviewableTask)
        ? '/knowledge-base?tab=objections'
        : draftReviewHref(reviewableTask._id, 'faqs'),
    },
    {
      key: 'landmines',
      label: 'Landmines',
      status: landminesStatus,
      detail: `${landmineCardReady} of ${landmineCardTotal} ready`,
      href: activeLandmines > 0 || !reviewableTask || nestedDraftCount(reviewableTask, 'unpublishedLandminesCount', 'suggestedLandminesCount') === 0
        ? '/knowledge-base?tab=landmines'
        : draftReviewHref(reviewableTask._id, 'landmines'),
    },
    {
      key: 'competitors',
      label: 'Competitors',
      status: competitorsStatus,
      detail: `${competitorCardReady} of ${competitorCardTotal} profiles fully detailed`,
      href: activeCompetitors > 0 || !reviewableTask || nestedDraftCount(reviewableTask, 'unpublishedCompetitorsCount', 'suggestedCompetitorsCount') === 0
        ? `/knowledge-base?tab=competitors${incompleteCompetitors > 0 ? '&focus=incomplete-competitors' : ''}`
        : draftReviewHref(reviewableTask._id, 'competitors'),
    },
    {
      key: 'playbooks',
      label: 'Playbooks',
      status: effectivePlaybooksStatus,
      detail: `${activePlaybooks} published playbook${activePlaybooks === 1 ? '' : 's'}`,
      href: activePlaybooks > 0 || !reviewableTask?.onboardingDraft?.starterPlaybookTaskId
        ? '/knowledge-base?tab=playbooks'
        : draftReviewHref(reviewableTask._id, 'playbooks'),
    },
  ];

  // --- Needs attention -------------------------------------------------------

  const needsAttention: NeedsAttentionItem[] = [];

  if (reviewableTask) {
    const recoveredCompletedDraft = reviewableTask.status === 'completed';
    const retainedAfterPublication = recoveredCompletedDraft || hasPublishedKnowledge;
    needsAttention.push({
      id: 'kg-drafts',
      title: retainedAfterPublication
        ? 'Saved knowledge suggestions are ready to review'
        : hasDraftCompanyContext && draftClaimsToVerify > 0
        ? `Review drafted answers and verify ${draftClaimsToVerify} company claim${draftClaimsToVerify === 1 ? '' : 's'} before publishing`
        : hasDraftCompanyContext
        ? hasCompleteReviewableDraftCompanyContext
          ? hasDraftAnswers(reviewableTask)
            ? 'Review drafted answers before publishing'
            : 'Review starter-set drafts before publishing'
          : 'Review drafted answers and your company profile before publishing'
        : 'Apexia Labs drafted answers that are waiting for your review',
      ctaLabel: retainedAfterPublication ? 'Review suggestions' : 'Review draft',
      section: 'imports',
      href: draftReviewHref(
        reviewableTask._id,
        retainedAfterPublication
          ? hasDraftAnswers(reviewableTask)
            ? 'faqs'
            : nestedDraftCount(reviewableTask, 'unpublishedLandminesCount', 'suggestedLandminesCount') > 0
            ? 'landmines'
            : 'competitors'
          : hasDraftCompanyContext && draftClaimsToVerify > 0
          ? 'claims'
          : 'company_context'
      ),
      kind: retainedAfterPublication ? 'optional' : 'required',
    });
  }

  if (pendingLandmines > 0) {
    needsAttention.push({
      id: 'pending-landmines',
      title: `${pendingLandmines} landmine${pendingLandmines === 1 ? '' : 's'} require acknowledgement before publication`,
      ctaLabel: 'Review landmines',
      section: 'landmines',
      href: reviewableTask ? draftReviewHref(reviewableTask._id, 'landmines') : undefined,
      kind: 'required',
    });
  }

  if (landminesMissingSafeGuidance > 0) {
    needsAttention.push({
      id: 'incomplete-legal-risk',
      title: `${landminesMissingSafeGuidance} Legal Risk ${landminesMissingSafeGuidance === 1 ? 'entry needs' : 'entries need'} both a Cannot Say phrase and a Can Say response`,
      ctaLabel: 'Complete Legal Risk',
      section: 'landmines',
      kind: 'optional',
    });
  }
  if (detractorsMissingCoachingTip > 0) {
    needsAttention.push({
      id: 'incomplete-detractors',
      title: `${detractorsMissingCoachingTip} Detractor ${detractorsMissingCoachingTip === 1 ? 'needs' : 'entries need'} a coaching tip before it is ready`,
      ctaLabel: 'Complete Detractors',
      section: 'landmines',
      kind: 'optional',
    });
  }

  const thinObjections = objectionsNeedingVariations;
  const thinFaqs = faqsNeedingVariations;
  if (thinObjections > 0) {
    needsAttention.push({
      id: 'thin-objections',
      title: `${thinObjections} objection${thinObjections === 1 ? '' : 's'} could use alternate phrasings so Apexia Labs recognizes them more often`,
      ctaLabel: 'Add variations',
      section: 'answers',
      answersFilter: 'objections',
      kind: 'optional',
    });
  }
  if (thinFaqs > 0) {
    needsAttention.push({
      id: 'thin-faqs',
      title: `${thinFaqs} FAQ${thinFaqs === 1 ? '' : 's'} could use alternate phrasings so Apexia Labs recognizes them more often`,
      ctaLabel: 'Add variations',
      section: 'answers',
      answersFilter: 'faqs',
      kind: 'optional',
    });
  }

  const savedObjectionSuggestions = reviewableTask
    ? reviewableTask.unpublishedDraftCount != null
      ? reviewableTask.unpublishedObjectionsCount || 0
      : reviewableTask.suggestedObjectionsCount || 0
    : 0;
  const savedFaqSuggestions = reviewableTask
    ? reviewableTask.unpublishedDraftCount != null
      ? reviewableTask.unpublishedFaqsCount || 0
      : reviewableTask.suggestedFaqsCount || 0
    : 0;
  if (answersNeedingVariations === 0 && objectionCoverageShortfall > savedObjectionSuggestions) {
    const remainingShortfall = objectionCoverageShortfall - savedObjectionSuggestions;
    needsAttention.push({
      id: 'objection-coverage',
      title: `Add ${remainingShortfall} more objection${remainingShortfall === 1 ? '' : 's'} for baseline buyer-question coverage`,
      ctaLabel: 'Add objections',
      section: 'answers',
      answersFilter: 'objections',
      kind: 'optional',
    });
  }
  if (answersNeedingVariations === 0 && faqCoverageShortfall > savedFaqSuggestions) {
    const remainingShortfall = faqCoverageShortfall - savedFaqSuggestions;
    needsAttention.push({
      id: 'faq-coverage',
      title: `Add ${remainingShortfall} more FAQ${remainingShortfall === 1 ? '' : 's'} for baseline buyer-question coverage`,
      ctaLabel: 'Add FAQs',
      section: 'answers',
      answersFilter: 'faqs',
      kind: 'optional',
    });
  }

  if (incompleteCompetitors > 0) {
    needsAttention.push({
      id: 'incomplete-competitors',
      title: `${incompleteCompetitors} competitor${incompleteCompetitors === 1 ? ' is' : 's are'} missing where you win or where they're weaker`,
      ctaLabel: 'Add competitor details',
      section: 'competitors',
      kind: 'optional',
    });
  }

  if (trainingStatus !== 'ready' && !hasDraftCompanyContext) {
    needsAttention.push({
      id: 'company-context',
      title: 'Add structured company details so Apexia Labs understands your business',
      ctaLabel: 'Add company details',
      section: 'overview',
      href: '/knowledge-base?tab=overview&focus=company-details',
      kind: 'required',
    });
  }

  // A starter playbook already sitting in the reviewable draft is not "no
  // playbook yet" — it's a draft waiting on the same review the 'kg-drafts'
  // item above already points at. Nudging to "add" one here as well would
  // tell the admin the opposite of what's true.
  const hasUnreviewedStarterPlaybook = Boolean(reviewableTask?.onboardingDraft?.starterPlaybookTaskId);
  if (activePlaybooks === 0 && !hasUnreviewedStarterPlaybook) {
    needsAttention.push({
      id: 'no-playbooks',
      title: "A call playbook would be a useful next improvement",
      ctaLabel: 'Add playbook',
      section: 'playbooks',
      kind: 'optional',
    });
  }

  const auditAttention: NeedsAttentionItem[] = auditFindings.map((finding) => ({
    id: `audit-${finding._id}`,
    title: displayAuditText(finding.title, finding.sourceItems),
    ctaLabel: 'Review',
    section: 'overview',
    href: `/knowledge-base?tab=overview&finding=${encodeURIComponent(finding._id)}`,
    kind: 'required',
    attentionType: 'live_guidance',
  }));

  return {
    loading,
    error,
    draftError,
    auditError,
    cards,
    needsAttention: [...auditAttention, ...needsAttention.map((item) => ({ ...item, attentionType: 'coverage' as const }))],
    auditFindings,
    kgTasks,
    seededTask,
    reviewableTask,
    activeDraftTask,
    liveAnswerCount: answersTotal,
    hasLiveCompanyContext,
    hasDraftCompanyContext,
    canCreatePlaybook,
    refresh,
  };
}
